import { useEffect, useMemo, useState } from "react";

/**
 * FORCE animated hero background — a diverse workforce lineup.
 * Dark mode shows a moody orange/black lineup; light mode swaps to a bright,
 * airy daytime lineup so the whole hero feels lighter.
 * Readability scrims keep the hero text and glass login window legible.
 */
export default function SkylineBackground() {
    const [isLight, setIsLight] = useState(
        () => typeof document !== "undefined" && document.documentElement.classList.contains("light")
    );

    useEffect(() => {
        const el = document.documentElement;
        const update = () => setIsLight(el.classList.contains("light"));
        update();
        const obs = new MutationObserver(update);
        obs.observe(el, { attributes: true, attributeFilter: ["class"] });
        return () => obs.disconnect();
    }, []);

    const wideSrc = isLight ? "/workforce-wide-light.jpg" : "/workforce-wide.jpg";
    const tallSrc = isLight ? "/workforce-tall-light.jpg" : "/workforce-tall.jpg";

    const embers = useMemo(
        () =>
            Array.from({ length: 18 }, () => ({
                left: Math.random() * 100,
                delay: Math.random() * 9,
                dur: 6 + Math.random() * 9,
                size: 2 + Math.random() * 3,
            })),
        []
    );

    return (
        <div
            className="fixed inset-0 z-0 overflow-hidden bg-force-bg"
            data-testid="workforce-background"
            aria-hidden="true"
        >
            {/* Workforce photography — <picture> fetches ONLY the wide (tablet+)
                OR tall (phone) image per device, not both (saves ~750KB first paint). */}
            <picture>
                <source media="(min-width: 768px)" srcSet={wideSrc} />
                <img
                    src={tallSrc}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover object-top md:object-center kenburns"
                    draggable={false}
                    fetchPriority="high"
                />
            </picture>

            {/* Readability scrims (theme-aware so light mode stays bright) */}
            {isLight ? (
                <>
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(245,242,236,0.58), rgba(245,242,236,0.12) 45%, rgba(245,242,236,0.92))" }} />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(245,242,236,0.55), rgba(245,242,236,0) 42%, rgba(245,242,236,0.72))" }} />
                </>
            ) : (
                <>
                    <div className="absolute inset-0 bg-gradient-to-b from-force-bg/85 via-force-bg/40 to-force-bg" />
                    <div className="absolute inset-0 bg-gradient-to-r from-force-bg/75 via-transparent to-force-bg/85" />
                </>
            )}

            {/* Pulsing warm glow */}
            <div
                className="glow-pulse-layer absolute inset-0"
                style={{
                    background:
                        "radial-gradient(60% 55% at 50% 62%, rgba(255,122,0,0.22), transparent 70%)",
                    mixBlendMode: isLight ? "multiply" : "screen",
                    animation: "glow-pulse 6s ease-in-out infinite",
                }}
            />

            {/* Drifting embers */}
            {embers.map((e, i) => (
                <span
                    key={i}
                    className="ember-particle absolute bottom-0 rounded-full bg-orange-400"
                    style={{
                        left: `${e.left}%`,
                        width: e.size,
                        height: e.size,
                        boxShadow: "0 0 8px rgba(255,122,0,0.9)",
                        animation: `ember-rise ${e.dur}s linear ${e.delay}s infinite`,
                    }}
                />
            ))}
        </div>
    );
}
